import { CircularProgress, Typography } from '@mui/material'
import { FraudProtectMajor, PromoteMinor } from '@shopify/polaris-icons'
import { Formik, FormikProps } from 'formik'
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { FormikRadioGroup } from 'src/components/shared/form/formik/FormikRadioGroup'
import { FormikMultipleFileInput } from 'src/components/shared/form/formik/FormikMultipleFileInput'
import { InsuranceCoverageFormValues } from './InsuranceCoverageForm.interface'
import { InfoContainer } from '../shared/InfoContainer'
import { createInsuranceCoverageSchema } from './InsuranceCoverageForm.validator'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { hasTextContent } from 'src/utils/htmlUtils'
import {
  FetchDocusignSigningEnvelopeOptions,
  DocusignCompleteSigning,
  DocusignDeclineSigning,
  DocusignStartSigning,
  FetchDriver,
  FetchOptOutProducts,
  UploadInsuranceCoverage,
} from 'src/constants/actionTypes'
import { CLIENT_ROUTES } from 'src/constants/routes'
import {
  DocusignSigningEnvelopeOptionsResponse,
  DocusignSigningField,
  DocusignStartSigningResponse,
} from 'src/interfaces/docusign'
import { OptOutProduct } from 'src/interfaces/optOut'
import { useQuery } from 'src/utils/hooks/useQuery'
import { captureSentryError } from 'src/utils/sentry'
import { showToast } from 'src/utils/toast'
import tw, { css } from 'twin.macro'
import { DocusignFieldsFormModal } from '../docusign-field-form-modal/DocusignFieldsFormModal'
import { CollectDataStepIndex } from '../client/CollectDataStepIndex'
import { Button } from '../shared/Button'
import { FormErrorMessage } from '../shared/form/FormErrorMessage'

interface Props {
  stepId?: string
  stepInsurancePolicyProductId?: string
  stepRequireDocumentUploadWhenOptedIn?: boolean
  documentUploadTitleWhenOptedIn?: string
}

const coverageOptions = [
  { label: 'I choose GigSafe insurance', value: 'gigsafe' },
  { label: 'I want to use my own insurance', value: 'own' },
]

const DOCUSIGN_CONTAINER_ID = 'docusign-container'

interface SigningField {
  tabLabel: string
  value: string
}

const InsuranceCoverageFormComponent = ({
  stepId,
  stepInsurancePolicyProductId,
  stepRequireDocumentUploadWhenOptedIn,
  documentUploadTitleWhenOptedIn,
}: Props) => {
  const dispatch = useDispatch()
  const { getQuery } = useQuery()
  const navigate = useNavigate()

  // START DOCUSIGN STUFF
  const [docusignSigningData, setDocusignSigningData] =
    useState<DocusignStartSigningResponse | null>(null)
  const [signingEnvelopeOptions, setSigningEnvelopeOptions] =
    useState<DocusignSigningEnvelopeOptionsResponse | null>(null)
  const [isStartLoading, setIsStartLoading] = useState<boolean>(false)
  const [isSigningFallbackLoading, setIsSigningFallbackLoading] = useState<boolean>(false)
  const [isFieldsModalOpen, setIsFieldsModalOpen] = useState<boolean>(false)
  const [modalFields, setModalFields] = useState<Array<DocusignSigningField>>([])
  const [modalHiddenFields, setModalHiddenFields] = useState<Array<DocusignSigningField>>([])
  const [signingIframeCrashed, setSigningIframeCrashed] = useState<boolean>(false)

  const signingIframeLoadCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const signingIframeCorsCheckIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const signingIframeExistsCheckIntervalRef = useRef<NodeJS.Timeout | null>(null)

  const docusignReturnEvent = getQuery('event')

  useEffect(() => {
    if (!stepId || !docusignReturnEvent) {
      return
    }

    if (docusignReturnEvent === 'signing_complete') {
      navigate(CLIENT_ROUTES.DOCS_URL)

      handleCompleteSigning()
    }
  }, [stepId, docusignReturnEvent])

  const handleLoadDocusignSigningEnvelopeOptions = useCallback(async () => {
    try {
      const response: DocusignSigningEnvelopeOptionsResponse | undefined = await createAsyncAction(
        dispatch,
        FetchDocusignSigningEnvelopeOptions.request({
          stepId,
        }),
      )

      if (!response) {
        throw new Error('Signing envelope options are not available')
      }

      setSigningEnvelopeOptions(response)
    } catch (err) {
      // ignore this error
    }
  }, [stepId])

  useEffect(() => {
    handleLoadDocusignSigningEnvelopeOptions()
  }, [stepId])

  const handleStartSigning = useCallback(
    async (signingFields?: Array<SigningField>) => {
      setIsStartLoading(true)

      try {
        const signingResponse: DocusignStartSigningResponse | undefined = await createAsyncAction(
          dispatch,
          DocusignStartSigning.request({
            signingFields,
            hiddenFields: modalHiddenFields,
            stepId,
            embedded: true,
          }),
        )

        if (!signingResponse) {
          throw new Error('Signing is not available')
        }

        if (signingResponse.retryFetchDriver) {
          await createAsyncAction(dispatch, FetchDriver.request())

          return
        }

        if (signingResponse.signingFields?.length) {
          setModalFields(signingResponse.signingFields)
          setModalHiddenFields(signingResponse.hiddenFields || [])
          setIsFieldsModalOpen(true)
          setIsStartLoading(false)

          return
        }

        setDocusignSigningData(signingResponse)
        handleLoadDocusign(signingResponse)
      } catch (err) {
        captureSentryError(err, { message: '[InsuranceCoverageForm.handleStartSigning]' })

        showToast('Failed to load signing page, please try again later', {
          variant: 'error',
        })
      } finally {
        setIsStartLoading(false)
      }
    },
    [stepId, modalHiddenFields],
  )

  const handleStartFallbackSigning = useCallback(async () => {
    setIsSigningFallbackLoading(true)

    try {
      const signingResponse: DocusignStartSigningResponse | undefined = await createAsyncAction(
        dispatch,
        DocusignStartSigning.request({
          stepId,
          embedded: false,
        }),
      )

      if (!signingResponse) {
        throw new Error('Signing is not available')
      }

      if (signingResponse.retryFetchDriver) {
        await createAsyncAction(dispatch, FetchDriver.request())

        return
      }

      if (signingResponse.signingData?.url) {
        window.open(signingResponse.signingData.url, '_blank')
      } else {
        throw new Error('Signing is not available')
      }
    } catch (err) {
      captureSentryError(err, { message: '[InsuranceCoverageForm.handleStartFallbackSigning]' })

      showToast('Failed to load signing page, please try again later', {
        variant: 'error',
      })
    } finally {
      setIsSigningFallbackLoading(false)
    }
  }, [stepId])

  const handleCompleteSigning = useCallback(async () => {
    try {
      await createAsyncAction(dispatch, DocusignCompleteSigning.request({ stepId }))
    } catch (err) {
      showToast('Failed to finish signing, please try again later', {
        variant: 'error',
      })
    }

    await createAsyncAction(dispatch, FetchDriver.request())
  }, [stepId])

  const handleDeclineSigning = useCallback(async () => {
    try {
      await createAsyncAction(dispatch, DocusignDeclineSigning.request({ stepId }))
      await createAsyncAction(dispatch, FetchDriver.request())
    } catch (err) {
      // ignore this error
    }
  }, [stepId])

  const handleLoadDocusign = useCallback(async (payload: DocusignStartSigningResponse) => {
    if (!payload.signingData?.url) {
      return
    }

    try {
      const docusign = await window.DocuSign.loadDocuSign(
        process.env.REACT_APP_DOCUGIGN_INTEGRATION_KEY as string,
      )

      const signing = docusign.signing({
        url: payload.signingData.url,
        displayFormat: 'focused',
        style: {
          branding: {
            primaryButton: {
              backgroundColor: '#333',
              color: '#fff',
            },
          },
          signingNavigationButton: {
            finishText: 'Continue',
            position: 'bottom-center',
          },
        },
      })

      signing.on('ready', () => {
        setIsStartLoading(false)
      })

      signing.on('sessionEnd', async (event) => {
        if (event.sessionEndType === 'signing_complete') {
          handleCompleteSigning()

          return
        }

        if (event.sessionEndType === 'ttl_expired') {
          setDocusignSigningData(null)
          showToast('Signing link is expired, please start signing again', {
            variant: 'error',
          })

          return
        }

        if (event.sessionEndType === 'decline') {
          await handleDeclineSigning()
          setDocusignSigningData(null)
          showToast('Signing is declined, please start signing again or contact support', {
            variant: 'error',
          })

          return
        }
      })

      signing.mount(`#${DOCUSIGN_CONTAINER_ID}`)

      startSigningIframeListeners()
    } catch (err: any) {
      setIsStartLoading(false)
      captureSentryError(err, {
        message: '[handleLoadDocusign]',
      }) //
    }
  }, [])

  const startSigningIframeListeners = useCallback(() => {
    let iframe = document.getElementById('js-library-iframe')

    if (!iframe) {
      const devDocusignIframeId = document.querySelector('[id^="js-library-iframe-"]')?.id

      if (devDocusignIframeId) {
        const devIframe = document.getElementById(devDocusignIframeId)

        if (devIframe) {
          iframe = devIframe
        }
      }
    }

    if (!iframe) {
      setSigningIframeCrashed(true)

      captureSentryError(new Error('Docusign iframe is not found'), {
        message: '[startSigningIframeListeners]',
      })

      return
    }

    let isIframeLoaded = false

    iframe.addEventListener('load', () => {
      isIframeLoaded = true
    })

    signingIframeLoadCheckTimeoutRef.current = setTimeout(() => {
      if (!isIframeLoaded) {
        setSigningIframeCrashed(true)

        captureSentryError(new Error('Docusign iframe is not loaded'), {
          message: '[startSigningIframeListeners]',
        })
      }
    }, 10000)

    iframe.addEventListener('error', () => {
      setSigningIframeCrashed(true)

      captureSentryError(new Error('Docusign iframe is crashed'), {
        message: '[startSigningIframeListeners]',
      })
    })

    signingIframeExistsCheckIntervalRef.current = setInterval(() => {
      let iframe = document.getElementById('js-library-iframe')

      if (!iframe) {
        const devDocusignIframeId = document.querySelector('[id^="js-library-iframe-"]')?.id

        if (devDocusignIframeId) {
          const devIframe = document.getElementById(devDocusignIframeId)

          if (devIframe) {
            iframe = devIframe
          }
        }
      }

      if (!iframe) {
        setSigningIframeCrashed(true)

        captureSentryError(new Error('Docusign iframe is not found'), {
          message: '[startSigningIframeListeners]',
        })
      }
    }, 3000)
  }, [])

  useEffect(() => {
    if (!signingIframeCrashed) {
      return
    }

    if (signingIframeLoadCheckTimeoutRef.current) {
      clearTimeout(signingIframeLoadCheckTimeoutRef.current)
    }

    if (signingIframeCorsCheckIntervalRef.current) {
      clearInterval(signingIframeCorsCheckIntervalRef.current)
    }

    if (signingIframeExistsCheckIntervalRef.current) {
      clearInterval(signingIframeExistsCheckIntervalRef.current)
    }

    showToast('Failed to start signing, please try again', {
      variant: 'error',
    })
  }, [signingIframeCrashed])

  const handleCloseFieldsModal = useCallback(() => {
    setIsFieldsModalOpen(false)
  }, [])

  const handleSubmitFieldsModal = useCallback(
    async (signingFields?: Array<SigningField>) => {
      await handleStartSigning(signingFields)

      setIsFieldsModalOpen(false)
    },
    [handleStartSigning],
  )
  // END DOCUSIGN STUFF

  const [optOutProduct, setOptOutProduct] = useState<OptOutProduct | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const handleFormSubmit = async (values: InsuranceCoverageFormValues) => {
    if (!stepId) {
      return
    }

    setIsLoading(true)
    setError(null)

    const requestBody: any = {
      optOut: values.type === 'own',
      stepId,
    }

    if (values.files && values.files.length > 0) {
      requestBody.files = values.files
    }

    try {
      await createAsyncAction(dispatch, UploadInsuranceCoverage.request(requestBody))
    } catch (err: any) {
      setError(err)
    } finally {
      setIsLoading(false)
    }
  }

  const formikRef = useRef<FormikProps<InsuranceCoverageFormValues>>(null)

  const handleClick = async (values: InsuranceCoverageFormValues) => {
    if (values.type === 'gigsafe' && optOutProduct?.docusignTemplateId) {
      if (stepRequireDocumentUploadWhenOptedIn) {
        await handleFormSubmit(values)
      }

      handleStartSigning()

      return
    }

    await handleFormSubmit(values)
  }

  useEffect(() => {
    const fetchProducts = async () => {
      const product: OptOutProduct = await createAsyncAction(
        dispatch,
        FetchOptOutProducts.request({
          stepId,
          productId: stepInsurancePolicyProductId,
        }),
      )

      setOptOutProduct(product)
    }

    fetchProducts()
  }, [stepId, stepInsurancePolicyProductId])

  const initialValues: InsuranceCoverageFormValues = useMemo(() => {
    let type: 'gigsafe' | 'own' | null = null

    if (!optOutProduct?.allowOptOut) {
      type = 'gigsafe'
    }

    return {
      type,
      files: [],
    }
  }, [optOutProduct?.allowOptOut])

  const insuranceExplanation = useMemo(
    () => optOutProduct?.explanation || optOutProduct?.insuranceProduct.explanation,
    [optOutProduct?.explanation, optOutProduct?.insuranceProduct.explanation],
  )

  const insuranceDescription = useMemo(
    () => optOutProduct?.description || optOutProduct?.insuranceProduct.description,
    [optOutProduct?.description, optOutProduct?.insuranceProduct.description],
  )

  const validationSchema = useMemo(
    () => createInsuranceCoverageSchema(stepRequireDocumentUploadWhenOptedIn),
    [stepRequireDocumentUploadWhenOptedIn],
  )

  if (!optOutProduct) {
    return <CircularProgress />
  }

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      onSubmit={handleClick}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ handleSubmit, values, setFieldValue }) => {
        const handleTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const newType = e.target.value

          if (newType !== values.type) {
            setFieldValue('files', null)
          }
          setFieldValue('type', newType)
        }

        const hasDocumentUploadTitleContent = hasTextContent(documentUploadTitleWhenOptedIn)

        return (
          <div css={tw`h-full w-full flex flex-col justify-between`}>
            <div>
              <div
                css={tw`w-11 h-11 flex justify-center items-center [border-radius: 22px] bg-shades-100 mt-2 mb-4`}
              >
                <FraudProtectMajor width={20} />
              </div>
              <CollectDataStepIndex stepId={stepId} />
              <Typography css={tw`my-2`} variant="h2">
                {optOutProduct?.insuranceProduct.name || 'Insurance Coverage'}
              </Typography>
              <Typography css={tw`mb-2 text-shades-400`} variant="body1">
                {insuranceDescription}
              </Typography>
              <a
                css={tw`no-underline text-primary-500 flex flex-row items-center mb-6`}
                href="https://www.gigsafe.com/terms"
                target="_blank"
              >
                <Typography
                  css={tw`cursor-pointer no-underline inline-block mr-1`}
                  variant="caption"
                >
                  View GigSafe terms and conditions
                </Typography>
                <PromoteMinor width={20} css={tw`fill-primary-500`} />
              </a>
              <Typography css={tw`mb-12 text-shades-400`}>
                {signingEnvelopeOptions?.signingEnvelopeAvailable ? (
                  <Typography css={tw`mt-4 text-shades-400`}>
                    If you are experiencing issues with the signing process, please try{' '}
                    <strong>Sign the contract in a new tab</strong> instead.
                  </Typography>
                ) : null}
              </Typography>
              {optOutProduct?.allowOptOut && (
                <div css={tw`flex flex-col p-4 rounded-2xl bg-shades-0 mb-4`}>
                  {optOutProduct?.allowOptOut && (
                    <div css={tw`mb-4`}>
                      <FormikRadioGroup
                        label="Select preferable option"
                        name="type"
                        options={coverageOptions}
                        onChange={handleTypeChange}
                      />
                    </div>
                  )}
                  {values.type === 'own' && (
                    <div>
                      {insuranceExplanation && (
                        <InfoContainer className={tw`mb-6`} color="orange">
                          <div
                            css={styles.descriptionContainer}
                            dangerouslySetInnerHTML={{
                              __html: insuranceExplanation,
                            }}
                          />
                        </InfoContainer>
                      )}
                      {stepRequireDocumentUploadWhenOptedIn && hasDocumentUploadTitleContent && (
                        <InfoContainer className={tw`mb-4`} color="orange">
                          <div
                            css={styles.descriptionContainer}
                            dangerouslySetInnerHTML={{
                              __html: documentUploadTitleWhenOptedIn,
                            }}
                          />
                        </InfoContainer>
                      )}

                      <div css={tw`bg-shades-0 p-0 rounded-xl mt-2`}>
                        <FormikMultipleFileInput name="files" label="Upload proof of coverage" />
                      </div>
                    </div>
                  )}
                </div>
              )}
              {stepRequireDocumentUploadWhenOptedIn && values.type && values.type !== 'own' && (
                <div css={tw`flex flex-col p-4 rounded-2xl bg-shades-0 mb-4`}>
                  {hasDocumentUploadTitleContent && (
                    <InfoContainer className={tw`mb-4`} color="orange">
                      <div
                        css={styles.descriptionContainer}
                        dangerouslySetInnerHTML={{
                          __html: documentUploadTitleWhenOptedIn,
                        }}
                      />
                    </InfoContainer>
                  )}
                  <FormikMultipleFileInput name="files" label="Upload proof of insurance" />
                </div>
              )}
            </div>
            <FormErrorMessage css={tw`mb-4`} error={error} />
            {signingIframeCrashed && values.type === 'gigsafe' ? (
              <Button
                className={tw`w-full`}
                loading={isSigningFallbackLoading}
                color="light"
                onClick={handleStartFallbackSigning}
              >
                Sign the contract in a new tab
              </Button>
            ) : (
              <Button
                className={tw`w-full mt-4`}
                loading={isStartLoading || isLoading}
                onClick={() => {
                  handleSubmit()
                }}
              >
                Submit and continue
              </Button>
            )}

            {!signingIframeCrashed ? (
              <div
                id={DOCUSIGN_CONTAINER_ID}
                css={[
                  tw`fixed top-0 left-0 h-full w-full bg-shades-0 overflow-auto`,
                  docusignSigningData ? undefined : tw`opacity-0 hidden`,
                ]}
              />
            ) : null}
            <DocusignFieldsFormModal
              isOpen={isFieldsModalOpen}
              fields={modalFields}
              onClose={handleCloseFieldsModal}
              onSubmit={handleSubmitFieldsModal}
            />
          </div>
        )
      }}
    </Formik>
  )
}

export const InsuranceCoverageForm = memo(InsuranceCoverageFormComponent)

const styles = {
  descriptionContainer: css`
    width: 100%;
    overflow-wrap: break-word;
    word-wrap: break-word;
    word-break: break-word;
    img {
      width: 90%;
      height: auto;
    }
    p,
    span,
    div,
    * {
      color: var(--mui-warning-500) !important;
      margin: 0;
      overflow-wrap: break-word;
      word-wrap: break-word;
      word-break: break-word;
      max-width: 100%;
    }
    p:first-child {
      margin-top: 0;
    }
    p:last-child {
      margin-bottom: 0;
    }
  `,
}
